import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useILScore } from '@/hooks/useILScore';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { 
  Flame, 
  TrendingDown, 
  TrendingUp, 
  Loader2, 
  AlertCircle, 
  Activity,
  Apple,
  Moon,
  Brain,
  Dumbbell,
  Wine,
  Cigarette,
  Droplets,
  Users,
  Heart,
  ArrowRight,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const factorIcons = {
  diet: Apple,
  smoking: Cigarette,
  sleep: Moon,
  activity: Dumbbell,
  stress: Brain,
  alcohol: Wine,
  hydration: Droplets,
  social: Users,
  hrv: Heart
};

const factorColors = {
  low: 'text-primary border-primary/30 bg-primary/10',
  moderate: 'text-secondary border-secondary/30 bg-secondary/10',
  high: 'text-destructive border-destructive/30 bg-destructive/10'
};

export default function InflammationHub() {
  const { data, isLoading, todayScore, sevenDayAvg, computeScore, isComputing } = useILScore(30);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="Inflammation Hub" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!todayScore) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="Inflammation Hub" />
        <div className="container mx-auto px-4 py-6 sm:py-8 max-w-4xl">
          <Card className="p-6 sm:p-8 text-center">
            <Flame className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-base sm:text-lg font-medium mb-2 text-foreground">No Inflammation Data Yet</h3>
            <p className="text-sm sm:text-base text-foreground/80 font-light mb-6 leading-relaxed">
              Start logging your lifestyle data to see your Inflammatory Load score and personalised protocols
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-left">
              <div className="p-4 border border-border rounded-lg bg-card/50">
                <h4 className="font-medium mb-2 flex items-center gap-2 text-foreground text-sm sm:text-base">
                  <Apple className="w-5 h-5 text-primary" />
                  Log Your Food
                </h4>
                <p className="text-sm text-foreground/70 font-light mb-3 leading-relaxed">
                  Track UPF levels, sugar intake, and omega-3 consumption
                </p>
                <Button size="sm" onClick={() => navigate('/nutrition')}>
                  Go to Food Logger
                </Button>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Moon className="w-5 h-5 text-primary" />
                  Daily Check-In
                </h4>
                <p className="text-sm text-foreground/70 font-light mb-3">
                  Log sleep, stress, alcohol, hydration, and social connection
                </p>
                <Button size="sm" onClick={() => navigate('/daily-check-in')}>
                  Complete Check-In
                </Button>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-primary" />
                  Connect WHOOP
                </h4>
                <p className="text-sm text-foreground/70 font-light mb-3">
                  Auto-sync HRV, recovery, sleep, and activity data
                </p>
                <Button size="sm" variant="outline" onClick={() => navigate('/settings?tab=integrations')}>
                  Connect Device
                </Button>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Cigarette className="w-5 h-5 text-primary" />
                  Track Lifestyle
                </h4>
                <p className="text-sm text-foreground/70 font-light mb-3">
                  Log smoking status and other inflammation factors
                </p>
                <Button size="sm" variant="outline" onClick={() => navigate('/daily-check-in')}>
                  Add Data
                </Button>
              </div>
            </div>

            <Button onClick={() => computeScore(undefined)} disabled={isComputing} className="w-full">
              {isComputing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Generate My IL Score
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const bucket = todayScore.il_score < 34 ? 'low' : todayScore.il_score < 67 ? 'moderate' : 'high';
  const bucketConfig = {
    low: { 
      label: 'Low', 
      color: 'text-primary',
      bg: 'bg-primary/10',
      description: 'Excellent! Your lifestyle patterns support healthy inflammation levels.'
    },
    moderate: { 
      label: 'Moderate', 
      color: 'text-accent',
      bg: 'bg-accent/10',
      description: 'Some factors may be contributing to inflammation. Small changes can help.'
    },
    high: { 
      label: 'High', 
      color: 'text-destructive',
      bg: 'bg-destructive/10',
      description: 'Multiple factors are contributing to inflammation. Let\'s address these together.'
    }
  };

  const change7d = sevenDayAvg ? todayScore.il_score - sevenDayAvg : 0;

  // All 9 factors with data completeness
  const allFactors = [
    { 
      key: 'diet', 
      label: 'Diet (UPF/Sugar)', 
      score: todayScore.diet_score,
      hasData: todayScore.diet_score !== 0.5,
      dataSource: 'Food Logger',
      link: '/nutrition'
    },
    { 
      key: 'smoking', 
      label: 'Smoking/Tobacco', 
      score: todayScore.smoking_score,
      hasData: todayScore.smoking_score !== 0,
      dataSource: 'Daily Check-In',
      link: '/daily-check-in'
    },
    { 
      key: 'sleep', 
      label: 'Sleep Quality', 
      score: todayScore.sleep_score,
      hasData: todayScore.sleep_score !== 0.5,
      dataSource: 'Daily Check-In or WHOOP',
      link: '/daily-check-in'
    },
    { 
      key: 'activity', 
      label: 'Physical Activity', 
      score: todayScore.activity_score,
      hasData: todayScore.activity_score !== 0.5,
      dataSource: 'WHOOP or Manual Log',
      link: '/whoop-dashboard'
    },
    { 
      key: 'stress', 
      label: 'Chronic Stress', 
      score: todayScore.stress_score,
      hasData: todayScore.stress_score !== 0.5,
      dataSource: 'Daily Check-In',
      link: '/daily-check-in'
    },
    { 
      key: 'alcohol', 
      label: 'Alcohol Intake', 
      score: todayScore.alcohol_score,
      hasData: todayScore.alcohol_score !== 0,
      dataSource: 'Daily Check-In',
      link: '/daily-check-in'
    },
    { 
      key: 'hydration', 
      label: 'Hydration', 
      score: todayScore.hydration_score,
      hasData: todayScore.hydration_score !== 0.5,
      dataSource: 'Daily Check-In',
      link: '/daily-check-in'
    },
    { 
      key: 'social', 
      label: 'Social Connection', 
      score: todayScore.social_connection_score,
      hasData: todayScore.social_connection_score !== 0.4,
      dataSource: 'Daily Check-In',
      link: '/daily-check-in'
    },
    { 
      key: 'hrv', 
      label: 'HRV (Optional)', 
      score: todayScore.hrv_score || 0,
      hasData: todayScore.hrv_score !== null,
      dataSource: 'WHOOP Device',
      link: '/settings?tab=integrations'
    }
  ].sort((a, b) => b.score - a.score);

  const topThree = allFactors.slice(0, 3);
  const missingDataCount = allFactors.filter(f => !f.hasData && f.key !== 'hrv').length;

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Inflammation Hub" />
      <div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-medium text-foreground mb-2">Inflammation Hub</h1>
              <p className="text-sm sm:text-base text-foreground/80 font-light leading-relaxed">
                Your complete inflammation overview and action plan
              </p>
            </div>
            <Button 
              onClick={() => computeScore(undefined)} 
              disabled={isComputing}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {isComputing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Refresh Score
            </Button>
          </div>

          {missingDataCount > 0 && (
            <Alert className="border-accent/30 bg-accent/5">
              <AlertCircle className="h-4 w-4 text-accent" />
              <AlertDescription className="text-sm">
                You're missing data for {missingDataCount} factor{missingDataCount > 1 ? 's' : ''}. 
                Complete your tracking for more accurate results.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Main Score Card */}
          <Card className="lg:col-span-1 p-4 sm:p-6 bg-card">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Flame className={`w-5 h-5 sm:w-6 sm:h-6 ${bucketConfig[bucket].color}`} />
                <Badge className={`${bucketConfig[bucket].bg} text-xs sm:text-sm`}>
                  {bucketConfig[bucket].label} Inflammation
                </Badge>
              </div>

              <motion.div
                className={`text-5xl sm:text-6xl font-medium ${bucketConfig[bucket].color} mb-2`}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, type: 'spring' }}
              >
                {Math.round(todayScore.il_score)}
              </motion.div>

              <p className="text-sm sm:text-base text-foreground/80 font-light mb-6 leading-relaxed">
                {bucketConfig[bucket].description}
              </p>

              {sevenDayAvg && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  {change7d > 0 ? (
                    <TrendingUp className="w-4 h-4 text-destructive" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-primary" />
                  )}
                  <span className={`${change7d > 0 ? 'text-destructive' : 'text-primary'} font-light`}>
                    {change7d > 0 ? '+' : ''}{change7d.toFixed(1)} from 7-day average
                  </span>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-border text-left">
                <p className="text-xs text-foreground/60 font-light mb-2">Last updated</p>
                <p className="text-sm font-medium text-foreground">{format(new Date(todayScore.date), 'dd MMM yyyy')}</p>
              </div>
            </div>
          </Card>

          {/* Top 3 Contributors */}
          <Card className="lg:col-span-2 p-4 sm:p-6 bg-card">
            <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 text-foreground">Your Top 3 Contributors</h3>
            <p className="text-sm text-foreground/70 font-light mb-4 sm:mb-6 leading-relaxed">
              These factors are contributing most to your inflammation. Address these first for maximum impact.
            </p>

            <div className="space-y-4">
              {topThree.map((factor, index) => {
                const Icon = factorIcons[factor.key as keyof typeof factorIcons] || Activity;
                const scorePercent = Math.round(factor.score * 100);
                const level = scorePercent < 33 ? 'low' : scorePercent < 67 ? 'moderate' : 'high';

                  return (
                    <div key={factor.key} className={`p-3 sm:p-4 border rounded-lg ${factorColors[level]}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="p-1.5 sm:p-2 rounded-lg bg-background/50">
                            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-sm sm:text-base text-foreground">#{index + 1}: {factor.label}</p>
                            <p className="text-xs font-light opacity-80">
                              {factor.hasData ? (
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Data from {factor.dataSource}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <XCircle className="w-3 h-3" />
                                  No data - using neutral score
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs sm:text-sm">
                          {scorePercent}%
                        </Badge>
                      </div>
                      <Progress value={scorePercent} className="h-2 mb-3" />
                    {!factor.hasData && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate(factor.link)}
                        className="w-full"
                      >
                        Add {factor.label} Data <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            <Button 
              onClick={() => navigate('/longevity')} 
              className="w-full mt-6"
            >
              View Protocols to Reduce These Factors
            </Button>
          </Card>
        </div>

        {/* All 9 Factors Grid */}
        <Card className="p-4 sm:p-6 mb-6 sm:mb-8 bg-card">
          <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 text-foreground">Complete Factor Breakdown</h3>
          <p className="text-sm text-foreground/70 font-light mb-4 sm:mb-6 leading-relaxed">
            All 9 inflammation factors ranked by your personal contribution. Lower scores are better.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {allFactors.map((factor) => {
              const Icon = factorIcons[factor.key as keyof typeof factorIcons] || Activity;
              const scorePercent = Math.round(factor.score * 100);
              const level = scorePercent < 33 ? 'low' : scorePercent < 67 ? 'moderate' : 'high';

              return (
                <div 
                  key={factor.key} 
                  className={`p-3 sm:p-4 border rounded-lg transition-all hover:shadow-md cursor-pointer ${factorColors[level]}`}
                  onClick={() => navigate(factor.link)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    <Badge variant="outline" className="font-mono text-xs">
                      {scorePercent}
                    </Badge>
                  </div>
                  <p className="font-medium text-sm mb-1 text-foreground">{factor.label}</p>
                  <Progress value={scorePercent} className="h-1.5 mb-2" />
                  <p className="text-xs font-light opacity-75">
                    {factor.hasData ? `From ${factor.dataSource}` : 'No data logged'}
                  </p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer bg-card" onClick={() => navigate('/daily-check-in')}>
            <Moon className="w-6 h-6 sm:w-8 sm:h-8 text-primary mb-3" />
            <h4 className="font-medium text-sm sm:text-base mb-2 text-foreground">Daily Check-In</h4>
            <p className="text-xs sm:text-sm text-foreground/70 font-light mb-4 leading-relaxed">
              Log sleep, stress, alcohol, hydration, and social connection
            </p>
            <Button size="sm" variant="outline" className="w-full text-xs sm:text-sm">
              Complete Today <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
            </Button>
          </Card>

          <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer bg-card" onClick={() => navigate('/nutrition')}>
            <Apple className="w-6 h-6 sm:w-8 sm:h-8 text-primary mb-3" />
            <h4 className="font-medium text-sm sm:text-base mb-2 text-foreground">Food Logger</h4>
            <p className="text-xs sm:text-sm text-foreground/70 font-light mb-4 leading-relaxed">
              Track UPF levels, sugar, fibre, and omega-3 intake
            </p>
            <Button size="sm" variant="outline" className="w-full text-xs sm:text-sm">
              Log Food <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
            </Button>
          </Card>

          <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer bg-card" onClick={() => navigate('/longevity')}>
            <TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-primary mb-3" />
            <h4 className="font-medium text-sm sm:text-base mb-2 text-foreground">Start Protocol</h4>
            <p className="text-xs sm:text-sm text-foreground/70 font-light mb-4 leading-relaxed">
              7-day plans to reduce your top inflammation drivers
            </p>
            <Button size="sm" className="w-full text-xs sm:text-sm">
              View Protocols <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
            </Button>
          </Card>
        </div>

        {/* Disclaimer */}
        <Alert className="mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs font-light">
            <strong>Educational Tool:</strong> This IL score is based on lifestyle data, not a medical test. 
            Always consult your GP if you have symptoms or concerns about inflammation.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
